import { useState, useEffect } from "react";
import { SaveOutlined, RestartAltOutlined } from "@mui/icons-material";
import { getLandingPageStats, updateLandingPageStats } from "../../../../services/Dashboard/Table";

const DEFAULT_STATS = {
  winRate: "33.4",
  placeRate: "68.2",
  hotPicksWinRate: "43.7",
  hotPicksPlaceRate: "76.5",
};

export default function PredictionStatsModule() {
  const [form, setForm] = useState({
    winRate: "",
    placeRate: "",
    hotPicksWinRate: "",
    hotPicksPlaceRate: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const fetchStats = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const data = await getLandingPageStats();
      if (data) {
        setForm({
          winRate: data.win_rate ? data.win_rate.replace("%", "") : DEFAULT_STATS.winRate,
          placeRate: data.place_rate ? data.place_rate.replace("%", "") : DEFAULT_STATS.placeRate,
          hotPicksWinRate: data.hot_picks_win_rate ? data.hot_picks_win_rate.replace("%", "") : DEFAULT_STATS.hotPicksWinRate,
          hotPicksPlaceRate: data.hot_picks_place_rate ? data.hot_picks_place_rate.replace("%", "") : DEFAULT_STATS.hotPicksPlaceRate,
        });
      } else {
        setForm(DEFAULT_STATS);
      }
    } catch (error) {
      setErrorMessage("Failed to load statistics from the backend server.");
      setForm(DEFAULT_STATS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setSuccessMessage("");
    setErrorMessage("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const statsPayload = {
        win_rate: `${form.winRate}%`,
        place_rate: `${form.placeRate}%`,
        hot_picks_win_rate: `${form.hotPicksWinRate}%`,
        hot_picks_place_rate: `${form.hotPicksPlaceRate}%`,
      };

      await updateLandingPageStats(statsPayload);
      setSuccessMessage("Prediction statistics updated successfully!");
    } catch (error) {
      setErrorMessage(error?.error || error?.detail || "Failed to save statistics to the database.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default statistics?")) {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");
      try {
        const defaults = {
          win_rate: `${DEFAULT_STATS.winRate}%`,
          place_rate: `${DEFAULT_STATS.placeRate}%`,
          hot_picks_win_rate: `${DEFAULT_STATS.hotPicksWinRate}%`,
          hot_picks_place_rate: `${DEFAULT_STATS.hotPicksPlaceRate}%`,
        };
        await updateLandingPageStats(defaults);
        setForm(DEFAULT_STATS);
        setSuccessMessage("Reset to default statistics successfully.");
      } catch (error) {
        setErrorMessage("Failed to reset statistics.");
      } finally {
        setSaving(false);
      }
    }
  };

  const fieldClass = "w-full px-4 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-bg)] text-[var(--color-text)] xl:text-base md:text-sm text-xs outline-none";
  const labelClass = "grid gap-2 text-[var(--color-text-muted)] md:text-sm text-xs font-bold uppercase tracking-[0.04em]";

  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[--color-surface]">
        <div className="flex flex-col gap-1">
          <p className="md:text-sm text-xs label-caps block m-0">Dynamic Statistics</p>
          <h4 className="xl:text-xl md:text-lg text-base text-[var(--color-text)]">
            Manage Landing Page Stats
          </h4>
          <p className="xl:text-base md:text-sm text-xs text-[var(--color-text-muted)]">
            Edit the numbers displayed in the stats section on the main landing page.
          </p>
        </div>
        <button
          type="button"
          onClick={handleReset}
          disabled={loading || saving}
          className="inline-flex items-center gap-1.5 min-h-[38px] px-3 py-2.5 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-transparent text-[var(--color-text)] cursor-pointer md:text-sm text-xs font-bold disabled:cursor-not-allowed"
        >
          <RestartAltOutlined sx={{ fontSize: 17 }} />
          Reset to Defaults
        </button>
      </div>

      {successMessage && (
        <div className="px-4 py-3.5 border border-[rgba(200,255,0,0.25)] bg-[rgba(200,255,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-primary)] sm:text-sm text-xs">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="px-4 py-3.5 border border-[rgba(255,69,0,0.25)] bg-[rgba(255,69,0,0.12)] rounded-[var(--radius-md)] text-[var(--color-accent-red)] md:text-sm text-xs">
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)] p-4 grid md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="skeleton h-[70px] bg-[rgba(255,255,255,0.04)]" />
          ))}
        </div>
      ) : (
        <form onSubmit={handleSave} className="grid gap-4 p-4 border border-[var(--color-border)] rounded-[var(--radius-md)] bg-[var(--color-surface)]">
          <div className="grid md:grid-cols-2 gap-4">
            <label className={labelClass}>
              Win Rate (%)
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.winRate}
                onChange={(e) => handleChange("winRate", e.target.value)}
                required
                className={fieldClass}
              />
            </label>

            <label className={labelClass}>
              Place Rate (%)
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.placeRate}
                onChange={(e) => handleChange("placeRate", e.target.value)}
                required
                className={fieldClass}
              />
            </label>

            <label className={labelClass}>
              Hot Picks Win Rate (%)
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.hotPicksWinRate}
                onChange={(e) => handleChange("hotPicksWinRate", e.target.value)}
                required
                className={fieldClass}
              />
            </label>

            <label className={labelClass}>
              Hot Picks Place Rate (%)
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.hotPicksPlaceRate}
                onChange={(e) => handleChange("hotPicksPlaceRate", e.target.value)}
                required
                className={fieldClass}
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center justify-self-start gap-2 min-h-[42px] px-3 py-2.5 border border-[var(--color-primary)] rounded-[var(--radius-md)] bg-[var(--color-primary)] text-black md:text-sm text-xs font-black cursor-pointer"
          >
            <SaveOutlined sx={{ fontSize: 18 }} />
            {saving ? "Saving..." : "Save Statistics"}
          </button>
        </form>
      )}
    </div>
  );
}
